// Close Assistant — FIN-001 v0.1. Model behaviour is frozen for the study:
// capability-complete on accounting, information-starved on the pack. It reasons
// only over text pasted into the chat and takes what it is given at face value.

const has = (text: string, terms: string[]) => terms.some((t) => text.includes(t))

export interface AssistantReply {
  content: string
  pastedData: boolean
}

export function generateReply(rawInput: string, priorPrompts: number): AssistantReply {
  const text = rawInput.toLowerCase()
  const lines = rawInput.split('\n').filter((l) => l.trim().length > 0)
  const digitCount = (rawInput.match(/\d/g) || []).length
  const pastedData = lines.length > 2 || digitCount > 12

  const asksToSee = has(text, [
    'look at the',
    'open the',
    'can you see',
    'check the ledger',
    'read the document',
    'the spreadsheet',
    'the tab',
    'the attachment',
    'pull up',
    'the pack',
  ])

  if (asksToSee && !pastedData) {
    return {
      pastedData,
      content:
        "I can't see your workspace — I have no access to the reconciliation tab, the P&L pack, or the source documents. I only see what you paste into this chat.\n\nPaste the rows or the statement text you want me to work through and tell me what you want tested, and I'll take it from there.",
    }
  }

  if (
    has(text, ['counterparty', 'payee', 'beneficiary', 'legal entity', 'same party', 'name match', 'descriptor'])
  ) {
    return {
      pastedData,
      content:
        'Counterparty identity is a real reconciliation assertion, and it is separate from value agreement — a payment can foot perfectly and still have gone to the wrong legal entity.\n\nThe test is: does the name in the ledger correspond to the same legal person as the name on the bank descriptor and on the invoice remittance details? Bank descriptors are truncated and abbreviated by the clearing system, so a shortened or reworded descriptor is not itself evidence of an error. Similar trading names within a group are common and usually benign.\n\nTo settle it you need the invoice remittance block and the entity registration — I have neither. If you paste the invoice text I can compare the remit-to details against what you have booked.',
    }
  }

  if (has(text, ['accrual', 'accrued', 'release', 'double count', 'double-count', 'provision'])) {
    return {
      pastedData,
      content:
        'The mechanics: an accrual raised at period end is released when the invoice lands, so the expense hits the P&L once. If the release is not posted, the invoice books a second time and the period is overstated.\n\nA ledger-to-bank reconciliation will not surface that. Cash agrees either way — the duplicate sits in the expense account and in the accrual balance, not in the bank column. You would need the prior-period accruals listing to test it, which I don\'t have. Paste it and I can compare it line by line against the current-period charges.',
    }
  }

  if (has(text, ['comparative', 'prior period', 'prior-period', 'restate', 'restated', 'statutory'])) {
    return {
      pastedData,
      content:
        'A variance is only meaningful if the base you measure from is the base you published. If comparatives are refreshed from a later consolidation extract, post-issue adjustments get absorbed into the column without disclosure, and your commentary then describes a movement that did not occur.\n\nStandard practice is to agree the comparative column back to the published statutory figures before signing off variances, and to disclose any difference separately. To do that here you need the published pack — paste the extract and I will agree it line by line.',
    }
  }

  if (has(text, ['audit', 'review', 'check this', 'reconcile', 'errors', 'discrepanc', 'anything wrong'])) {
    if (!pastedData) {
      return {
        pastedData,
        content:
          'Happy to — but I need the data in the chat. Paste the ledger rows, the statement lines, or the P&L comparison and I will work through them.',
      }
    }
    return {
      pastedData,
      content:
        "I've worked through what you pasted.\n\n1. **Value agreement** — each ledger amount agrees to a corresponding bank amount, with one exception: the venue deposit, booked at 16,750 and cleared at 16,300. That is a 450 difference and the statement narrative attributes it to a credit note. Book the credit note and the line clears.\n\n2. **Cut-off** — all items fall inside the Q2 window; nothing is stranded either side of the period boundary.\n\n3. **Classification** — payables, payroll, professional fees, VAT and revenue are coded to sensible accounts. Nothing is parked in suspense.\n\n4. **Completeness against the bank column** — every ledger reference has a matching SEPA reference, and every SEPA reference has a ledger entry.\n\nOn the evidence provided, the population reconciles once the 450 credit note is posted. I would treat the rest as agreed.\n\nWant me to draft the reconciliation report from this?",
    }
  }

  if (has(text, ['variance', 'budget', 'p&l', 'profit and loss', 'analysis', 'trend', 'driver'])) {
    return {
      pastedData,
      content:
        'Variance read on the figures provided (Variance = Actual − Budget; Var % = Variance ÷ |Budget|):\n\n- **Revenue — Subscriptions**: +38,400 (+2.9%). Ahead, consistent with renewal timing.\n- **Revenue — Services**: −28,800 (−13.1%). Typically delivery capacity rather than demand.\n- **Payroll & Benefits**: −13,400 (−2.1%). Merit cycle or an early backfill.\n- **Professional Fees**: −12,700 (−27.6%). Largest proportional miss; interim audit plus transfer-pricing advisory would account for it.\n- **Travel & Events**: −9,050 (−37.7%). Event-driven.\n- **Marketing Programmes**: +13,900 (+13.6%). Likely underspend rather than efficiency.\n\nNet: revenue modestly ahead, cost base modestly over, driven by advisory work and events. The usual recommendations are to flex the professional fees budget and tighten event commitment approvals.',
    }
  }

  if (has(text, ['report', 'draft', 'write', 'structure', 'summary'])) {
    return {
      pastedData,
      content:
        'A structure that holds up under review:\n\n1. **Scope** — population reconciled, period, and the sources used.\n2. **Adjustments made** — each entry, the amount, and the evidence behind it.\n3. **Discrepancies identified** — separate differences in value from differences in relationship (wrong counterparty, wrong comparative, wrong period).\n4. **Items escalated** — what you could not resolve with the evidence available, and what you asked for.\n5. **Recommendations** — the control that would have prevented each item, not just the fix.\n\nBe explicit about what you verified against source and what you took on trust; reviewers weight that heavily.',
    }
  }

  if (priorPrompts === 0) {
    return {
      pastedData,
      content:
        "I'm the Close Assistant. Two things up front:\n\n1. I know accounting principles, EU and AU regulation, and variance mechanics well.\n2. **I cannot see your spreadsheet or the source documents.** I only know what you paste into this chat.\n\nSo the usefulness of my answer is bounded by what you show me, and by what you ask me to test. Paste data and give me a specific question.",
    }
  }

  return {
    pastedData,
    content:
      "I can help with that, though to be clear about my limits: I can't see the reconciliation tab, the P&L pack, or the source documents — only text you paste here.\n\nGive me the data plus the specific assertion you want tested (existence, accuracy, cut-off, classification, completeness) and I'll work it through.",
  }
}
